/**
 * `kind: 'gradeList'`. Each submission is the complete pass, so re-grading adjusts by the
 * difference. Prior rulings come from `spec.items[].current`, not local state, so another
 * host tab or a driving TV is never silently reverted. J/K move, Y/N rule and advance.
 */

import { useEffect, useState } from 'react';
import type { DecisionSpec, Verdict } from '@gsf/contract';
import type { RendererProps } from '../renderer';

type Spec = Extract<DecisionSpec, { kind: 'gradeList' }>;

function isTyping(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null;
  return element !== null && /^(INPUT|TEXTAREA|SELECT)$/.test(element.tagName);
}

export function GradeListCard({ request, spec, submit, disabled, slots }: RendererProps<Spec>) {
  const items = spec.items;
  const [cursor, setCursor] = useState(0);

  /** The pass as it stands, with one row changed. */
  const rule = (id: string, verdict: Verdict) => {
    const pass: Record<string, Verdict> = {};
    for (const item of items) if (item.current) pass[item.id] = item.current;
    pass[id] = verdict;
    submit(pass);
  };

  useEffect(() => {
    if (disabled || items.length === 0) return;
    function onKey(event: KeyboardEvent) {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTyping(event.target)) return;

      const key = event.key.toLowerCase();
      if (key === 'j' || key === 'arrowdown') {
        setCursor((at) => Math.min(items.length - 1, at + 1));
      } else if (key === 'k' || key === 'arrowup') {
        setCursor((at) => Math.max(0, at - 1));
      } else if (key === 'y' || key === 'n') {
        setCursor((at) => {
          const item = items[Math.min(at, items.length - 1)];
          if (item) rule(item.id, key === 'y' ? 'correct' : 'incorrect');
          return Math.min(items.length - 1, at + 1);
        });
      } else {
        return;
      }
      event.preventDefault();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // `rule` closes over `items`, which is in the deps; re-binding on a re-grade is
    // what keeps the pass current.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, items]);

  if (items.length === 0) {
    return <p className="faint">Nothing to grade yet.</p>;
  }

  return (
    <div className="col">
      <span className="faint">{request.prompt}</span>
      <ul className="answer-list">
        {items.map((item, index) => (
          <li
            key={item.id}
            className={[
              item.current === 'correct' ? 'is-correct' : '',
              item.current === 'incorrect' ? 'is-incorrect' : '',
              index === cursor ? 'is-cursor' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <div className="answer-main">
              <span className="answer-text">{item.label}</span>
              <span className="spacer" />
              {disabled ? null : (
                <>
                  <button
                    className="btn btn-sm btn-good"
                    type="button"
                    title="Correct (Y)"
                    aria-label="Correct"
                    onClick={() => {
                      setCursor(index);
                      rule(item.id, 'correct');
                    }}
                  >
                    ✓
                  </button>
                  <button
                    className="btn btn-sm btn-bad"
                    type="button"
                    title="Incorrect (N)"
                    aria-label="Incorrect"
                    onClick={() => {
                      setCursor(index);
                      rule(item.id, 'incorrect');
                    }}
                  >
                    ✗
                  </button>
                </>
              )}
              {slots?.itemAction?.(item.id)}
            </div>
            {slots?.itemPanel?.(item.id)}
          </li>
        ))}
      </ul>
    </div>
  );
}
